// Apolo IDE — Hermes live run store (module singleton, no React).
//
// The live event sink for the run board/detail: while the .app executor drives
// a run, the loop's events (token/reasoning/assistant/tool_call/...) stream in
// HERE for sub-second UI updates. The server PATCH (/api/hermes/runs/:id/state)
// is the durable record — this store is ONLY the live view, and is allowed to
// be lossy: a ring buffer caps each run at 500 events (oldest dropped).
//
// Mirrors agentRunStore's pattern: plain module singleton so it outlives every
// component; subscribers get the runId and re-read whatever slice they need.

const MAX_EVENTS = 500;

// Onda 1 (anti-travamento): cap de runs RESIDENTES. Antes o Map crescia 1 entrada
// por run PARA SEMPRE — `clearRun` só é chamado quando a detail view é dispensada,
// então uma sessão longa disparando muitos runs Hermes acumulava metadados + até
// 500 eventos por run indefinidamente. Agora é LRU: ao criar um run novo, os runs
// CONCLUÍDOS (não-live) mais antigos são dropados além do teto. Run vivo NUNCA é
// evictado (o executor ainda escreve nele). O registro durável vive no servidor.
const MAX_RESIDENT_RUNS = 20;

const runs = new Map(); // runId -> { events: [], live: false }  (ordem de inserção = idade)
const subs = new Set(); // listeners: (runId) => void

// Evicta runs concluídos (não-live) do mais antigo pro mais novo até voltar ao teto.
function evictOldCompleted() {
  if (runs.size <= MAX_RESIDENT_RUNS) return;
  for (const [id, r] of runs) {
    if (runs.size <= MAX_RESIDENT_RUNS) break;
    if (r.live) continue; // nunca evicta um run vivo
    runs.delete(id);
  }
}

function ensure(runId) {
  let r = runs.get(runId);
  if (!r) {
    r = { events: [], live: false };
    runs.set(runId, r);
    evictOldCompleted();
  }
  return r;
}

// Defensive: a throwing UI listener must never break the executor's path.
function emit(runId) {
  for (const cb of subs) {
    try {
      cb(runId);
    } catch {
      /* best-effort */
    }
  }
}

/** Append a loop event for a run. Ring buffer: beyond 500, the oldest drops. */
export function appendEvent(runId, evt) {
  const r = ensure(runId);
  r.events.push(evt);
  if (r.events.length > MAX_EVENTS) r.events.shift();
  emit(runId);
}

/** Current events for a run (a copy — safe to mutate). [] when unknown. */
export function getEvents(runId) {
  return [...(runs.get(runId)?.events || [])];
}

/** Mark a run as live (executor attached) or not. No-op when unchanged. */
export function setLive(runId, live) {
  const next = !!live;
  const existing = runs.get(runId);
  // setLive(unknown, false) is a no-op — don't conjure an empty entry just to
  // clear a flag that was never set (an entry-per-call leak otherwise).
  if (!existing) {
    if (!next) return;
    const r = ensure(runId);
    r.live = true;
    emit(runId);
    return;
  }
  if (existing.live === next) return;
  existing.live = next;
  emit(runId);
}

export function isLive(runId) {
  return !!runs.get(runId)?.live;
}

/** Ids of every run currently live (the board's pulsing dots). */
export function getLiveRunIds() {
  const ids = [];
  for (const [id, r] of runs) if (r.live) ids.push(id);
  return ids;
}

/**
 * Subscribe to store changes. Listener is called with the runId on any append
 * or live change. Returns the unsubscribe fn. No replay — read getEvents/
 * isLive on mount, then this streams subsequent changes.
 */
export function subscribe(listener) {
  subs.add(listener);
  return () => {
    subs.delete(listener);
  };
}

/** Drop a run's events + live flag (e.g. when its detail view is dismissed). */
export function clearRun(runId) {
  runs.delete(runId);
  emit(runId);
}

// Test-only: número de runs residentes (para asserções do LRU).
export function __residentRunCount() {
  return runs.size;
}

// Test-only: wipe everything between cases.
export function __resetHermesRuns() {
  runs.clear();
  subs.clear();
}
